import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { IconInfo } from "@aws-amplify/ui-react/internal";
import { CodeHighlight as Prism } from "@mantine/code-highlight";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Code,
  Flex,
  Image,
  List,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDebouncedValue, useInterval } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { graphql, ResultOf, VariablesOf } from "gql.tada";
import { GraphQLFormattedError } from "graphql";
import { findKey, forEach, isArray, map } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { useSelectedOrganizationId } from "../../../../../business/OrganizationId";
import { CreationForm } from "../../../components/form/CreationForm";
import { TagDefinitions } from "./available-tags";
import { PAGE_TEMPLATES } from "./page-templates";

const PlatformFilePublishMutation = graphql(`
  mutation platformFilePublish($id: ID!) {
    platformFilePublish(id: $id) {
      id
    }
  }
`);

const PlatformFileValidateFileUploadMutation = graphql(`
  mutation platformFileValidateFileUpload($id: ID!) {
    platformFileValidateFileUpload(id: $id) {
      id
    }
  }
`);

const PlatformFileGenerateUploadUrlMutation = graphql(`
  mutation platformFileGenerateUploadUrl($id: ID!, $size: Float!) {
    platformFileGenerateUploadUrl(id: $id, size: $size) {
      fields {
        value
        key
      }
      url
    }
  }
`);

const PlatformFileCreateMutation = graphql(`
  mutation platformFileCreate(
    $platformId: ID!
    $input: PlatformCdnFileCreateInput!
  ) {
    platformFileCreate(platformId: $platformId, input: $input) {
      id
    }
  }
`);

const PlatformFileQuery = graphql(`
  query PlatformFile($id: ID!) {
    platformFile(id: $id) {
      id
      prodCdnPath
      contents
      type
      devCdnPath
      name
    }
  }
`);

export class PlatformFileUploadError extends Error {
  constructor(public readonly errors: ReadonlyArray<GraphQLFormattedError>) {
    super();
    this.name = "PlatformFileUploadError";
  }
}

export const uploadFileToS3 = async (
  { url, fields }: { url: string; fields: { key: string; value: string }[] },
  contents: Blob,
) => {
  const formData = new FormData();
  formData.append("Content-Type", contents.type);
  forEach(fields, ({ key, value }) => {
    formData.append(key, value);
  });
  formData.append("file", contents);
  return fetch(url, {
    method: "POST",
    body: formData,
  });
};

export const usePlatformFileUpload = () => {
  const [generateUploadUrl] = useMutation(
    PlatformFileGenerateUploadUrlMutation,
  );
  const [validateFileUpload] = useMutation(
    PlatformFileValidateFileUploadMutation,
  );
  const [createFile] = useMutation(PlatformFileCreateMutation);
  const [publishFile] = useMutation(PlatformFilePublishMutation);
  const [getPlatformFile] = useLazyQuery(PlatformFileQuery);

  return async ({
    fileBlob,
    ...rest
  }: { fileBlob: Blob; publish: boolean } & (
    | { fileId: string }
    | {
        platformId: string;
        name: string;
        type: VariablesOf<typeof PlatformFileCreateMutation>["input"]["type"];
      }
  )): Promise<ResultOf<typeof PlatformFileQuery>["platformFile"]> => {
    if ("platformId" in rest) {
      const { data, error } = await createFile({
        variables: {
          platformId: rest.platformId,
          input: {
            name: rest.name,
            type: rest.type,
          },
        },
      });
      if (error) {
        throw new PlatformFileUploadError([error]);
      }
      (rest as unknown as { fileId: string }).fileId = data?.platformFileCreate
        .id as string;
    }

    if (!("fileId" in rest)) {
      notifications.show({
        title: "Error",
        message: "File ID is required",
        variant: "error",
        color: "red",
      });
      throw new PlatformFileUploadError([new Error("File ID is required")]);
    }

    const fileId = rest.fileId;

    const { data: signedUrlData, error } = await generateUploadUrl({
      variables: {
        id: fileId,
        size: fileBlob.size,
      },
    });

    if (error) {
      throw new PlatformFileUploadError([error]);
    }

    if (!signedUrlData?.platformFileGenerateUploadUrl) {
      throw new PlatformFileUploadError([
        new Error("Failed to generate upload URL"),
      ]);
    }

    await uploadFileToS3(
      {
        url: signedUrlData.platformFileGenerateUploadUrl.url as string,
        fields: signedUrlData.platformFileGenerateUploadUrl.fields,
      },
      fileBlob,
    );

    const { error: validateErrors } = await validateFileUpload({
      variables: {
        id: fileId,
      },
    });

    if (validateErrors) {
      throw new PlatformFileUploadError([validateErrors]);
    }

    if (rest.publish) {
      await publishFile({ variables: { id: fileId } });
    }

    const { data: fileData, error: fileError } = await getPlatformFile({
      variables: {
        id: fileId,
      },
    });

    if (fileError) {
      throw new PlatformFileUploadError([fileError]);
    }

    if (!fileData?.platformFile) {
      throw new PlatformFileUploadError([new Error("Failed to get file")]);
    }

    return fileData.platformFile;
  };
};

const fileExtensionToLanguageMap: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  json: "json",
  html: "html",
  css: "css",
  scss: "scss",
  liquid: "liquid",
  md: "markdown",
  yml: "yaml",
  yaml: "yaml",
};

export const PlatformFileContent = () => {
  const organizationId = useSelectedOrganizationId();
  const fileId = useParams().fileId as string;
  const [publishing, setPublishing] = useState(false);
  const [fileUpdated, setFileUpdated] = useState(false);
  const [fileLoading, setFileLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<string>();
  const [contents, setContents] = useState<string>("");
  const [debouncedContents] = useDebouncedValue(contents, 500, {
    leading: true,
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [hotUpdatesWindow, setHotUpdatesWindow] = useState<
    | {
        window: Window;
        ready: boolean;
        messageHandler: (message: MessageEvent) => void;
      }
    | undefined
  >();
  const hotUpdatesWindowRef = useRef<{
    window: Window;
    ready: boolean;
    messageHandler: (event: MessageEvent) => void;
  } | null>(null);
  const [publishFile] = useMutation(PlatformFilePublishMutation);
  const { data, loading, refetch } = useQuery(PlatformFileQuery, {
    variables: {
      id: fileId,
    },
  });
  const uploadToPlatform = usePlatformFileUpload();

  // Update the ref whenever the state changes
  const updateHotUpdatesWindow = (
    newWindow:
      | {
          window: Window;
          ready: boolean;
          messageHandler: (message: MessageEvent) => void;
        }
      | undefined,
  ) => {
    setHotUpdatesWindow(newWindow);
    hotUpdatesWindowRef.current = newWindow ?? null;
  };

  useEffect(() => {
    if (data?.platformFile?.type === "IMAGE") {
      setFileLoading(false);
      return;
    }

    if (data?.platformFile) {
      if (!data.platformFile.devCdnPath) {
        setContents(data.platformFile.contents ?? "");
        setFileLoading(false);
        return;
      }

      fetch(data.platformFile.devCdnPath)
        .then((response) => (response.ok ? response.text() : ""))
        .then((contents) => {
          setContents(contents);
          setFileLoading(false);
        })
        .catch((e) => {
          console.error("Failed to fetch upload URL", e);
          notifications.show({
            title: "Error",
            message: "Failed to fetch file",
            variant: "error",
            color: "red",
          });
        });
    }
  }, [data?.platformFile]);

  useEffect(() => {
    return () => {
      if (hotUpdatesWindow) {
        window.removeEventListener("message", hotUpdatesWindow.messageHandler);
      }
      childWindowCheckInterval.stop();
    };
  }, []);

  useEffect(() => {
    hotUpdatesWindowRef.current?.window.postMessage(
      {
        type: "updateContents",
        organizationId,
        contents: debouncedContents,
      },
      "*",
    );
  }, [debouncedContents]);

  const childWindowCheckInterval = useInterval(() => {
    if (hotUpdatesWindowRef.current?.window.closed) {
      childWindowCheckInterval.stop();
      window.removeEventListener(
        "message",
        hotUpdatesWindowRef.current.messageHandler,
      );
      updateHotUpdatesWindow(undefined);
    }
  }, 500);

  if (loading || fileLoading) {
    return <Loader />;
  }

  const fileExtension = data?.platformFile?.name.split(".").at(-1);
  const fileLanguage = fileExtension
    ? fileExtensionToLanguageMap[fileExtension]
    : undefined;
  return (
    <>
      {data?.platformFile?.type === "PAGE" && (
        <Select
          label={"Select starter template"}
          data={PAGE_TEMPLATES.map((template) => ({
            label: template.name,
            value: template.template,
          }))}
          onChange={(value) => {
            setSelectedTemplate(value ?? undefined);
            setContents(value ?? "");
          }}
        />
      )}
      <Flex>
        <Box
          key={selectedTemplate}
          style={{
            flex: "0 0 80%",
          }}
        >
          {data?.platformFile?.type !== "IMAGE" ? (
            <CreationForm
              title={"Update file contents"}
              schemaDefinition={{
                template: {
                  type: "CODE_EDITOR",
                  label: "File contents",
                  language:
                    data?.platformFile?.type === "PAGE" ||
                    data?.platformFile?.type === "COMPONENT"
                      ? "liquid"
                      : fileLanguage,
                  yupConfig: yup.string().required(),
                  defaultValue: selectedTemplate ?? contents,
                  value: contents,
                  onChange(contents) {
                    setContents(contents);
                  },
                },
              }}
              onSubmit={async (values) => {
                // Convert textarea content to a Blob
                const fileBlob = new Blob([values.template], {
                  type: "text/plain",
                });

                try {
                  await uploadToPlatform({
                    fileId,
                    fileBlob,
                    publish: false,
                  });
                } catch (e) {
                  notifications.show({
                    title: "Error",
                    message: (e as Error).message,
                    variant: "error",
                    color: "red",
                  });
                  return;
                }

                setFileUpdated(true);
                notifications.show({
                  title: "File updated",
                  message: "The file has been updated successfully",
                  color: "green",
                  autoClose: 3000,
                });
              }}
            />
          ) : (
            <>
              <Dropzone
                mb={10}
                loading={uploadingImage}
                accept={IMAGE_MIME_TYPE}
                multiple={false}
                onDrop={async (files) => {
                  setUploadingImage(true);
                  const fileBlob = files[0];
                  if (!fileBlob) {
                    return;
                  }

                  try {
                    await uploadToPlatform({
                      fileId,
                      fileBlob,
                      publish: false,
                    });
                    setUploadingImage(false);

                    notifications.show({
                      title: "Image uploaded",
                      message: "The image has been uploaded successfully",
                      color: "green",
                      autoClose: 3000,
                    });
                    await refetch();
                    setFileUpdated(true);
                  } catch (e) {
                    notifications.show({
                      title: "Error",
                      message: (e as Error).message,
                      variant: "error",
                      color: "red",
                    });
                  } finally {
                    setUploadingImage(false);
                  }
                }}
              >
                <Text ta="center">Drop new image here</Text>
              </Dropzone>
              {data?.platformFile?.devCdnPath && (
                <Image src={data.platformFile.devCdnPath} alt="Image" />
              )}
            </>
          )}
        </Box>
        <ScrollArea
          style={{
            width: "20%",
            height: "90vh",
            flex: "0 0 20%",
          }}
        >
          <Stack>
            <Button
              w={200}
              color={"green"}
              onClick={async () => {
                setPublishing(true);
                try {
                  await publishFile({ variables: { id: fileId } }).then(
                    (data) => {
                      if (data?.error) {
                        throw new Error(data.error.message);
                      }
                    },
                  );
                } catch (e) {
                  notifications.show({
                    title: "Cannot publish file",
                    message: (e as Error).message,
                    color: "red",
                    autoClose: 3000,
                  });
                  return;
                } finally {
                  setPublishing(false);
                }
                setFileUpdated(false);
                notifications.show({
                  title: "File published",
                  message: "The file has been published successfully",
                  color: "green",
                  autoClose: 3000,
                });
              }}
              loading={publishing}
              disabled={!fileUpdated}
            >
              Publish file to CDN
            </Button>
            {
              /*(data?.platformFile?.type === "PAGE" ||
              data?.platformFile?.type === "COMPONENT") && */ (data
                ?.platformFile?.type === "PAGE" ||
                data?.platformFile?.type === "COMPONENT") && (
                <>
                  <Button
                    w={200}
                    color={"blue"}
                    onClick={() => {
                      if (hotUpdatesWindowRef.current) {
                        hotUpdatesWindowRef.current.window.focus();
                        return;
                      }

                      const newWindow = window.open(
                        `${window.location.origin
                          .replace("console", "platform")
                          .replace("5173", "5174")}?hotUpdates`,
                        "_blank",
                        "width=800,height=600",
                      );

                      if (newWindow) {
                        const handleMessage = (event: MessageEvent) => {
                          if (
                            ![
                              "https://platform.well-played.gg",
                              "https://platform.stg.well-played.gg",
                            ].includes(event.origin) &&
                            !event.origin.startsWith("http://localhost")
                          ) {
                            /*                      console.warn(
                            "Ignoring message from unknown origin",
                            event.origin,
                          );*/
                            return;
                          }

                          if (event.data.type === "ready") {
                            updateHotUpdatesWindow({
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              ...hotUpdatesWindowRef.current!,
                              ready: true,
                            });

                            newWindow.postMessage(
                              {
                                type: "updateContents",
                                organizationId,
                                contents: contents,
                              },
                              "*",
                            );
                          }
                        };

                        updateHotUpdatesWindow({
                          window: newWindow,
                          messageHandler: handleMessage,
                          ready: false,
                        });
                        childWindowCheckInterval.start();

                        window.addEventListener("message", handleMessage);
                      } else {
                        modals.open({
                          title: "Error",
                          children: (
                            <>
                              Failed to open new window. Pop-up blockers might
                              be enabled
                            </>
                          ),
                        });
                      }
                    }}
                  >
                    Open debug window
                  </Button>
                  <Title order={2}>Introduction to Liquid</Title>
                  <Text>
                    Liquid is a simple, expressive and safe Shopify / Github
                    Pages compatible template engine.
                    <br />
                    Liquid syntax is relatively simple. There’re 2 types of
                    markups in Liquid:
                    <br />
                    <Text span fw={700}>
                      Tags.
                    </Text>{" "}
                    A tag consists of a tag name and optional arguments wrapped
                    between {`{ % and %}`}.<br />
                    <Text span fw={700}>
                      Outputs.
                    </Text>{" "}
                    An output consists of a value and a list of filters, which
                    is optional, wrapped between {`{{ and }}`}.
                    <Title order={4} mt={10}>
                      Outputs
                    </Title>
                    Outputs are used to output variables, which can be
                    transformed by filters, into HTML.
                    <br />
                    The following template will insert the value of username
                    into the input’s value:
                    <Prism
                      code={`<input type="text" name="user" value="{{username}}">`}
                      language={"html" as any}
                    />
                    Values in output can be transformed by filters before
                    output.
                    <br />
                    To append a string after the variable:
                    <Prism
                      code={`{{ username | append: ", welcome to Liquid!" }}`}
                      language={"html" as any}
                    />
                    Filters can be chained:
                    <Prism
                      code={`{{ username | append: ", welcome to Liquid!" | capitalize }}`}
                      language={"html" as any}
                    />
                    <Alert
                      variant="light"
                      color="blue"
                      title="Info"
                      icon={<IconInfo />}
                    >
                      The following outputs are already available and can be
                      used in your templates:
                      <br />
                      <List>
                        <List.Item>
                          <Code>params</Code>: the URL parameters defined using{" "}
                          <Code>:myParam</Code> format
                          <br />
                          Usage example: <Code>{`{{ params.myParam }}`}</Code>
                        </List.Item>
                      </List>
                    </Alert>
                    <Anchor
                      target="_blank"
                      href="https://liquidjs.com/filters/overview.html"
                    >
                      Default filters can be found here
                    </Anchor>
                    <Title order={4} mt={10}>
                      Tags
                    </Title>
                    Tags are used to control the template rendering process,
                    manipulating template variables, inter-op with other
                    templates, etc.
                    <br />
                    For example assign can be used to define a variable which
                    can be later used in the template:
                    <Prism
                      language={"html" as any}
                      code={`{% assign foo = "FOO" %}`}
                    />
                    Typically tags appear in pairs with a start tag and a
                    corresponding end tag. For example:
                    <Prism
                      code={`{% if foo == "FOO" %}
Variable "foo" equals "FOO"
{% else %}
Variable "foo" not equals "FOO"
{% endif %}`}
                      language={"html" as any}
                    />
                    <Anchor
                      target="_blank"
                      href="https://liquidjs.com/tags/overview.html"
                    >
                      Default tags can be found here
                    </Anchor>
                  </Text>
                  <Title>Url Parameters</Title>
                  If you defined a route with parameters (example:
                  /tournaments/:tournamentId), you can access these parameters
                  in your templates using the params object.
                  <br />
                  Usage example:
                  <Prism
                    code={`{% if params.tournamentId %}
    Tournament ID: {{ params.tournamentId }}
{% endif %}`}
                    language={"html" as any}
                  />
                  <Title>Tags</Title>
                  Usage example:
                  <br />
                  <Prism
                    code={`{% favicon "favicon.svg" %}
{% title "Home" %}

<ul>
  {% for item in array %}
    <li>{{ item.name }}</li>
  {% endfor %}
</ul>
`}
                    language={"html" as any}
                  />
                  <Stack>
                    {TagDefinitions.map((tag, idx) => (
                      <Stack gap={5} key={idx}>
                        <Stack gap={0}>
                          <Title order={4}>{tag.tag}</Title>
                          {tag.description}
                          <Text fw={700}>Example:</Text>
                          <Prism
                            language={"html" as any}
                            code={`{% ${tag.tag} ${
                              (tag.paramValue &&
                                `"param-value"${
                                  (tag.hashInputs && " ") ?? ""
                                }`) ??
                              ""
                            }${map(
                              tag.hashInputs,
                              (value: any) => `${value.name}:"value"`,
                            ).join(" ")} %}${
                              tag.scopeOutputs
                                ? `\n  ${map(
                                    tag.scopeOutputs,
                                    (schema: any, key: any) =>
                                      `{{ ${key}${
                                        (isArray(schema?.type) &&
                                          `.${
                                            findKey(
                                              schema.type,
                                              (value: any) =>
                                                (value as any).type ===
                                                  "String" ||
                                                (value as any).type ===
                                                  "String!",
                                            ) ??
                                            findKey(schema.type, () => true)
                                          }`) ||
                                        ""
                                      } }}`,
                                  ).join("\n  ")}\n{% end-${tag.tag} %}`
                                : ``
                            }`}
                          />
                        </Stack>
                        <Stack gap={0}>
                          {tag.paramValue && (
                            <>
                              <Text fw={700}>Param value:</Text>

                              <Text key={idx}>
                                <Text fw={700} span>
                                  {tag.paramValue.description}
                                  {tag.paramValue.required && "*"}
                                </Text>
                              </Text>
                            </>
                          )}
                          {tag.hashInputs && (
                            <>
                              <Text fw={700}>Hash inputs:</Text>
                              {map(tag.hashInputs, (value, idx) => (
                                <Text key={idx}>
                                  <Text fw={700} span>
                                    {value.name} -
                                  </Text>{" "}
                                  {value.type}:{" "}
                                  {
                                    (
                                      value as unknown as {
                                        description: string;
                                      }
                                    ).description
                                  }
                                </Text>
                              ))}
                            </>
                          )}
                          {tag.scopeOutputs && (
                            <>
                              <Text fw={700}>Scope outputs:</Text>
                              <Stack gap={0}>
                                {map(tag.scopeOutputs, (schema, key) => (
                                  <Text key={key}>
                                    <Text fw={700} span>
                                      {key}
                                    </Text>
                                    {" - "}
                                    {schema.typeName}
                                    {": "}
                                    {schema.description}
                                  </Text>
                                ))}
                              </Stack>
                            </>
                          )}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                  <Title>Forms</Title>
                  Forms allow you to mutate your data on the platform. They can
                  be used as a way to create, update or delete data.
                  <br />
                  They are mainly used to provide a way to call mutations on the
                  API.
                  <br />
                  By using forms, you can ask for user inputs, use hidden inputs
                  or simply create a button to register a team to a tournament.
                  <br />
                  Usage example:
                  <br />
                  <Prism
                    code={`
{% # This variable will be set if the form is submitted %}
{% if formRegisterdTeam %}
    {% if formRegisterdTeam.error %}
        <p>Errors: {{ formRegisterdTeam.error }}</p>
    {% else %}
        <p>Team registered successfully</p>
    {% endif %}
{% else %}
    {% capture mutation %}
    mutation registerTournamentTeam(
        $teamName: String!
        $teamTag: String!
        $customFields: [PropertyValueInput!]!
    ) {
      registerTournamentTeam(
        tournamentId: {{ params.tournamentId }}
        input: {name: $teamName, tag: $teamTag, customFields: $customFields}
      ) {
        id
      }
    }
    {% endcapture %}
    {% form mutation:mutation output-variable:"formRegisterdTeam" %}
      <input type="text" name="teamName" placeholder="Team name" />
      <input type="text" name="teamTag" placeholder="Team tag" />
      <input type="number"  name="custom-field-age" pattern="[0-9]*" placeholder="Your age">
      <input type="hidden" name="customFields[0].property" value="hello" />
      <input type="hidden" name="customFields[0].value" value="world" />
      <button type="submit">Submit</button>
    {% endform %}
{% endif %}`}
                    language={"html" as any}
                  />
                </>
              )
            }
          </Stack>
        </ScrollArea>
      </Flex>
    </>
  );
};
